import { UserStore } from "@mirage/store/UserStore";
import { IBaseEntity } from "@shared/mirage/core/contracts/IBaseEntity";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { IEntityPageResult } from "@shared/mirage/core/contracts/IEntityPageResult";
import { Required } from "@shared/mirage/core/contracts/Required";
import { GroupModel, IGroup, PermissionModel } from "@shared/mirage/core/contracts/IGroup";
import { AppLoginRequest, GetUserPermissionsRequest, GetUserPermissionsResult, LoginResult } from "@shared/mirage/core/contracts/User";
import { SessionTimeoutProps } from "@mirage/components/sessionTimeout/SessionTimeout";
import { DeleteRecordRequest, InsertRecordResult, UpdateRecordResult } from "@shared/mirage/repositories/IBaseRepository";
import {
    DashboardUrlRequest,
    DashboardUrlResponse,
    GetUserResult,
    GroupRecordRequest,
    GroupsPageRequest,
    IApplicationGroups,
    IApplicationService,
    IRefreshSessionRequest,
    IRefreshSessionResult,
    InsertUserRequest,
    RelatedListDeleteRecordRequest,
    RelatedListUpdateRecordRequest,
    RemoveUserFromGroupRequest,
    UpdateUserRequest,
} from "@shared/mirage/services/IApplicationService";
import { IErrorMessage } from "@shared/mirage/core/contracts/IErrorMessage";
import { BaseApiService } from "./BaseApiService";
import { IUser } from "@shared/mirage/core/contracts/IUser";
import { SessionManager } from "@mirage/manager/SessionManager";
import { GroupDto } from "@shared/mirage/services/IReportsService";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";

export interface ApplicationServiceProps {
    setSessionTimeout?: (props: Omit<SessionTimeoutProps, "open">) => void;
    logoutNavigate?: () => void;
}

export default class ApplicationService extends BaseApiService implements IApplicationService {
    private client: ApplicationServiceProps;
    constructor(props?: ApplicationServiceProps) {
        super("mirage/service");
        this.client = { ...props };
    }

    static _lastCheckTime: Date | null = null;
    static _lastResult = false;

    static SetLastAuthCheckResult(result: boolean): void {
        ApplicationService._lastCheckTime = new Date();
        ApplicationService._lastResult = result;
    }

    async checkAuth(): Promise<boolean> {
        const result = (await this.httpInstance().get("/users/checkAuth")).status === 200;
        ApplicationService.SetLastAuthCheckResult(result);
        return ApplicationService._lastResult;
    }
    async logout() {
        this.client.logoutNavigate && this.client.logoutNavigate();
        SessionManager.lastUserRequestTime = null;
        await this.httpInstance().post("/logout");
        localStorage.removeItem("users.user");
        sessionStorage.removeItem("mirage.ApplicationService.modules");
        ApplicationService.SetLastAuthCheckResult(false);
    }

    async login(loginRequest: AppLoginRequest): Promise<LoginResult> {
        const response = await this.httpInstance().post("/login", loginRequest);
        const result = response.data as LoginResult;
        if (result.userId && result.expiryDate) {
            localStorage.setItem(
                "users.user",
                JSON.stringify({
                    id: result.userId,
                    name: result.userFullName,
                    first_name: result.userName,
                    last_name: result.userLastName,
                    custom_properties: result.userCustomProperties,
                    expiryDate: DateFormatter.formatDateTimeToLocalTimeZoneAgnosticDateTimeString(result.expiryDate) as unknown as Date,
                    sessionTimeoutBuffer: result.sessionTimeoutBuffer,
                    email: result.userEmail,
                    subModules: result.subModules,
                    superSetUrl: result.superSetUrl,
                } as Omit<UserStore, "permissions">)
            );
            ApplicationService.SetLastAuthCheckResult(true);
        }
        return result;
    }

    private async onSessionTimeout() {
        if (!SessionManager.lastUserRequestTime) {
            SessionManager.clearSessionTimeouts();
            this.logout();
            return;
        }
        let currentAttempt = SessionManager._sessionRefreshRequestMaxAttempts;
        const newSessionDate = SessionManager.lastUserRequestTime;
        const { errorMsg } = await this.refreshSession({ newDate: newSessionDate, timeZone: DateFormatter.currentTimezone });
        if (!errorMsg) {
            this.prolongRoutine();
            return;
        }
        SessionManager._sessionAttemptIntervalId = setInterval(async () => {
            if (currentAttempt > 0) {
                const attempt = SessionManager._sessionRefreshRequestMaxAttempts + 1 - currentAttempt;
                const { errorMsg } = await this.refreshSession({ newDate: newSessionDate, timeZone: DateFormatter.currentTimezone, attempt });
                if (!errorMsg) {
                    this.prolongRoutine();
                    return;
                }
                currentAttempt--;
            } else {
                SessionManager.clearSessionTimeouts();
                this.logout();
            }
        }, 1000);
    }

    private sessionTimeoutCountDownModal(remainingTime: number) {
        if (SessionManager.lastUserRequestTime || !this.client.setSessionTimeout) {
            return;
        }
        if (remainingTime > SessionManager._sessionTimeoutBuffer) {
            this.client.setSessionTimeout({ timeoutIn: SessionManager._sessionTimeoutBuffer });
        } else if (remainingTime >= 3000) {
            this.client.setSessionTimeout({ timeoutIn: remainingTime });
        } else {
            this.logout();
        }
    }

    prolongRoutine() {
        const loggedInUser = this.getLoggedInUser();
        if (!loggedInUser) {
            return;
        }
        const { expiryDate } = loggedInUser;
        const sessionTimeout = expiryDate && new Date(expiryDate);
        if (!sessionTimeout) {
            return;
        }
        const todayDate = new Date();
        const remainingTime = sessionTimeout.getTime() - todayDate.getTime();
        const modalTimeoutMs = remainingTime - SessionManager._sessionTimeoutBuffer;
        const sessionTimeoutMs = remainingTime - 600;
        SessionManager.lastUserRequestTime = null;
        SessionManager.clearSessionTimeouts();
        SessionManager._sessionTimeoutId = setTimeout(() => this.onSessionTimeout(), sessionTimeoutMs);
        SessionManager._sessionModalTimeoutId = setTimeout(() => this.sessionTimeoutCountDownModal(remainingTime), modalTimeoutMs);
    }

    public setLoggedInUser(user: Partial<Omit<UserStore, "permissions">>) {
        const currentUser = this.getLoggedInUser();
        const newUser = { ...currentUser, ...user };
        localStorage.setItem("users.user", JSON.stringify(newUser));
    }

    public getLoggedInUser() {
        const str = localStorage.getItem("users.user");
        if (!str) {
            return null;
        }
        const user = JSON.parse(str) as Omit<UserStore, "permissions">;
        user.expiryDate = user.expiryDate && new Date(user.expiryDate);
        return user;
    }
    async getModules(): Promise<string[]> {
        const existingModules = sessionStorage.getItem("mirage.ApplicationService.modules");
        if (!existingModules) {
            const response = await this.httpInstance().get("/modules/");
            const modules = response.data;
            sessionStorage.setItem("mirage.ApplicationService.modules", JSON.stringify(modules));
            return modules;
        }
        return JSON.parse(existingModules);
    }

    async refreshSession(req: Omit<IRefreshSessionRequest, "userId">): Promise<Omit<IRefreshSessionResult, "token">> {
        const response = await this.httpInstance().post("/users/refreshSessionToken", req);
        const data = response.data as Omit<IRefreshSessionResult, "token">;
        const { expiryDate, errorMsg } = data as Omit<IRefreshSessionResult, "token">;
        if (!errorMsg) {
            this.setLoggedInUser({ expiryDate });
        }
        return data;
    }
    async relatedListPageRequest<T extends IBaseEntity>(pr: Required<IEntityPageRequest, "relatedListOptions">): Promise<IEntityPageResult<T>> {
        const response = await this.httpInstance().post("/relatedList/pageRequest", pr);
        return response.data;
    }
    async relatedListUpdateRecord<T extends IBaseEntity>(r: RelatedListUpdateRecordRequest<T>): Promise<UpdateRecordResult> {
        const response = await this.httpInstance().put("/relatedList/updateRecord", r);
        return response.data;
    }
    async relatedListDeleteRecord<T extends IBaseEntity>(r: RelatedListDeleteRecordRequest<T>): Promise<IErrorMessage> {
        const response = await this.httpInstance().delete(`/relatedList/deleteRecord/${r.recordId}`, { data: r });
        return response.data;
    }
    async groupsPageRequest(pr: GroupsPageRequest): Promise<IEntityPageResult<IGroup>> {
        const response = await this.httpInstance().post("/groups/pageRequest", pr);
        return response.data;
    }
    async applicationsPageRequest(pr: IEntityPageRequest): Promise<IEntityPageResult<IGroup>> {
        const response = await this.httpInstance().post("/applications/pageRequest", pr);
        return response.data;
    }
    async getApplicationsGroups(): Promise<IApplicationGroups[]> {
        const response = await this.httpInstance().get("/applicationsGroups");
        return response.data;
    }
    async getGroupById(id: number): Promise<GroupModel | null> {
        const response = await this.httpInstance().get("/groups/" + id);
        return response.data;
    }
    async insertGroup(insertGroupRequest: GroupRecordRequest): Promise<InsertRecordResult<IGroup>> {
        const response = await this.httpInstance().post("/groups", insertGroupRequest);
        return response.data;
    }
    async deleteGroup(request: DeleteRecordRequest): Promise<IErrorMessage> {
        const response = await this.httpInstance().delete("/groups/" + request.recordId);
        return response.data;
    }
    async updateGroup(request: GroupRecordRequest): Promise<UpdateRecordResult> {
        const response = await this.httpInstance().put("/groups/", request);
        return response.data;
    }
    async usersPageRequest(pr: IEntityPageRequest): Promise<IEntityPageResult<IGroup>> {
        const response = await this.httpInstance().post("/users/pageRequest", pr);
        return response.data;
    }
    async bankHolidaysPageRequest(pr: IEntityPageRequest): Promise<IEntityPageResult<IGroup>> {
        const response = await this.httpInstance().post("/bankHolidays/pageRequest", pr);
        return response.data;
    }
    async getUserById(id: number): Promise<GetUserResult | null> {
        const response = await this.httpInstance().get("/users/" + id);
        return response.data;
    }
    async insertUser(insertUserRequest: InsertUserRequest): Promise<InsertRecordResult<IUser>> {
        const response = await this.httpInstance().post("/users", insertUserRequest);
        return response.data;
    }
    async getUserPermissions(request: Omit<GetUserPermissionsRequest, "userId">): Promise<GetUserPermissionsResult> {
        const response = await this.httpInstance().post("/users/userPermissions", request);
        return response.data;
    }
    async deleteUser(request: DeleteRecordRequest): Promise<IErrorMessage> {
        const response = await this.httpInstance().delete("/users/" + request.recordId);
        return response.data;
    }
    async removeUserFromGroup(request: RemoveUserFromGroupRequest): Promise<IErrorMessage> {
        const response = await this.httpInstance().delete("/users/removeUserFromGroup", { data: request });
        return response.data;
    }
    async updateUser(request: UpdateUserRequest): Promise<UpdateRecordResult> {
        const response = await this.httpInstance().put("/users/" + request.recordId, request);
        return response.data;
    }
    async getGroupsByApplicationName(appName: string): Promise<GroupDto[]> {
        const response = await this.httpInstance().get(`/groupsByApp/${appName}`);
        return response.data;
    }
    async getPermissionsByModule(appName: string): Promise<PermissionModel[]> {
        const response = await this.httpInstance().get(`/permissions/${appName}`);
        return response.data;
    }
    async getDashboardUrl(req: DashboardUrlRequest): Promise<DashboardUrlResponse> {
        const response = await this.httpInstance().get(`/dashboard/` + req.appName);
        return response.data;
    }
}
